<template>
  <BContent
    v-if="isAvailableTriggerFeature"
    v-loading="$wait.is(['getTriggersWait', 'deleteTriggerWait'])"
    class="content"
  >
    <template #navbar>
      <div class="navbar-left">
        <span class="page-title fs-400 fw-bold">{{ $t('trigger.title') }}</span>
        <button
          v-if="authorize(['owner'])"
          type="button"
          class="create-btn"
          @click="handleClickNew"
        >
          <BIcon
            type="white"
            size="medium"
          >
            bolt
          </BIcon>
          <span class="ml-100">{{ $t('general.create.to', { item: $t('trigger.title') }) }}</span>
        </button>
      </div>
      <div class="navbar-right">
        <BPagination
          v-model="currentPage"
          :length="allPageCount"
          :total-visible="5"
        />
      </div>
    </template>
    <div class="select-wrapper mt-300 mb-200 pl-600">
      <SelectUser
        v-model="selectedUserId"
      />
    </div>
    <div class="mt-50">
      <TriggerList
        :triggers="triggers"
        @click:row="handleClickEdit"
        @click:duplicate="handleClickDuplicate"
        @after-delete="handleChangeTriggerList"
        @after-change-owner="handleChangeTriggerList"
      />
    </div>
  </BContent>
</template>

<script lang="ts" setup>
import { ref, onBeforeMount, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { PaginationMeta, Trigger, TriggerApiGetTriggersRequest } from '@/api/openapi';
import { TriggerApiService } from '@/api/user/resources/trigger';
import TriggerList from '@/components/organisms/user/triggers/TriggerList.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useAlert } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import { i18nGlobal as i18n } from '@/i18n';
import { key } from '@/store/index';

const { isAvailableTriggerFeature } = useAvailableFeatureCheck();
const { doActionWithWait } = useWait();
const { alert } = useAlert();
const api = new TriggerApiService();
const router = useRouter();
const store = useStore(key);
const { inject: injectCurrentUser } = useCurrentUserProvider();
const { authorize, currentUser } = injectCurrentUser();

const triggers = ref<Trigger[]>([]);
const currentPage = ref(1);
const meta = ref<PaginationMeta>({
  totalPages: 1,
  totalCount: 0,
  limitValue: 0,
  currentPage: 0,
  isEmpty: false,
});
const selectedUserId = ref<number | undefined>();
const totalCount = ref(0);

onBeforeMount(async () => {
  selectedUserId.value = currentUser.value.id;
  await fetchTriggers(currentPage.value);
});

watch(() => selectedUserId.value, () => {
  fetchTriggers(currentPage.value);
});

watch(() => currentPage.value, (newValue) => {
  fetchTriggers(newValue);
});

const allPageCount = computed(() => meta.value.totalPages);

const fetchTriggers = async (currentPage: number) => {
  const ownerId = selectedUserId.value;
  const request: TriggerApiGetTriggersRequest = {
    currentPage,
    ownerId,
  };
  await doActionWithWait('getTriggersWait', async () => {
    const { data } = await api.getTriggers({ request });
    triggers.value = data.triggers;
    meta.value = data.meta;
    totalCount.value = data.totalCount ?? 0;
  });
};

const isTriggerLimitExceeded = async () => {
  const { triggerLimit } = await store.dispatch('user/getClientLimitSettingAction');
  if (totalCount.value >= triggerLimit) {
    await alert({
      title: i18n.t('general.error'),
      message: i18n.t('trigger.exceededTriggerLimit'),
    });
    return true;
  }
  return false;
};

const handleClickNew = async () => {
  if (await isTriggerLimitExceeded()) return;
  router.push({ name: 'TriggerNew' });
};

const handleClickDuplicate = async (duplicateId: number) => {
  if (await isTriggerLimitExceeded()) return;
  router.push({ name: 'TriggerNew', query: { duplicateId } });
};

const handleClickEdit = (id: number) => {
  router.push({ name: 'Trigger', params: { id } });
};

const handleChangeTriggerList = () => {
  fetchTriggers(currentPage.value);
};
</script>

<style lang="scss" scoped>
.navbar-left {
  display: flex;
  height: 100%;
  margin-top: 3px;
  .create-btn {
    position: relative;
    width: 150px;
    height: 37px;
    margin-top: 5px;
    margin-left: 30px;
    color: $textcolor-white;
    background-color: $basecolor-primary;
    border-radius: 20px;
    outline: none;
    .b-icon {
      position: absolute;
      top: 10px;
      left: 15px;
    }
  }
  .page-title {
    display: block;
    margin-top: 12px;
  }
}
.navbar-right {
  max-width: 300px;
  margin-left: auto;
  margin-top: 3px;
}
.select-wrapper {
  position: sticky;
  left: 0;
  z-index: 3;
}
.content {
  position: relative;
}
</style>
